import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import CreateApiKeyModal from './CreateApiKeyModal';
import './ApiKeysSection.css';

function ApiKeysSection({ organizationId }) {
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        loadApiKeys();
    }, [organizationId]);

    const loadApiKeys = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await API.getApiKeys();
            if (response && response.data) {
                setApiKeys(response.data.api_keys || []);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeKey = async (keyId, keyName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir révoquer la clé "${keyName}" ?\n\nCette action est irréversible et les applications utilisant cette clé ne pourront plus s'authentifier.`)) {
            return;
        }

        try {
            await API.revokeApiKey(keyId);
            await loadApiKeys();
        } catch (error) {
            alert(error.message);
        }
    };

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        loadApiKeys();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const maskSecret = (prefix) => {
        return prefix + '****...';
    };

    const copyToClipboard = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        } catch (err) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        }
    };

    if (loading) {
        return <div className="apikeys-loading">Chargement des clés API...</div>;
    }

    if (error) {
        return <div className="apikeys-error">Erreur : {error}</div>;
    }

    return (
        <div className="apikeys-section">
            <div className="apikeys-header">
                <h2>Clés API ({apiKeys.filter(k => k.is_active).length})</h2>
                <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                    Créer une clé API
                </button>
            </div>

            <div className="apikeys-intro">
                <p>
                    Les clés API permettent aux applications tierces (comme le plugin Outlook)
                    de s'authentifier auprès de votre organisation sans utiliser de mot de passe.
                </p>
            </div>

            <div className="apikeys-warning">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p>
                    <strong>Important :</strong> La clé secrète n'est affichée qu'une seule fois
                    lors de la création. Conservez-la en lieu sûr.
                </p>
            </div>

            {apiKeys.length === 0 ? (
                <div className="apikeys-empty">
                    <p>Aucune clé API n'a encore été créée.</p>
                    <p>Créez une clé API pour permettre au plugin Outlook de se connecter.</p>
                </div>
            ) : (
                <div className="apikeys-table-container">
                    <table className="apikeys-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Clé API</th>
                                <th>Secret</th>
                                <th>Créée le</th>
                                <th>Dernière utilisation</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiKeys.map(key => (
                                <tr
                                    key={key.id}
                                    className={`clickable ${!key.is_active ? 'revoked' : ''}`}
                                    onClick={() => setSelectedKey(key)}
                                >
                                    <td>
                                        <div className="key-name">{key.name}</div>
                                        <div className="key-creator">
                                            Par {key.created_by.first_name} {key.created_by.last_name}
                                        </div>
                                    </td>
                                    <td>
                                        <code className="key-value">{key.api_key}</code>
                                    </td>
                                    <td>
                                        <code className="key-value secret">{maskSecret(key.api_secret_prefix)}</code>
                                    </td>
                                    <td>{formatDate(key.created_at)}</td>
                                    <td>
                                        {key.last_used_at ? (
                                            <span title={key.last_used_ip ? `IP: ${key.last_used_ip}` : ''}>
                                                {formatDate(key.last_used_at)}
                                            </span>
                                        ) : (
                                            <span className="never-used">Jamais utilisée</span>
                                        )}
                                    </td>
                                    <td>
                                        {key.is_active ? (
                                            <span className="status-badge active">Active</span>
                                        ) : (
                                            <span className="status-badge revoked">
                                                Révoquée le {formatDate(key.revoked_at)}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {key.is_active && (
                                            <button
                                                className="btn-danger-small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRevokeKey(key.id, key.name);
                                                }}
                                                title="Révoquer cette clé"
                                            >
                                                Révoquer
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreateModal && (
                <CreateApiKeyModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}

            {/* Modal de détails de la clé API */}
            {selectedKey && (
                <div className="modal-overlay" onClick={() => setSelectedKey(null)}>
                    <div className="modal-content modal-md" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Détails de la clé API</h2>
                            <button className="modal-close" onClick={() => setSelectedKey(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-row">
                                <label>Nom</label>
                                <div className="detail-value">{selectedKey.name}</div>
                            </div>

                            <div className="detail-row">
                                <label>Clé API</label>
                                <div className="detail-value-with-copy">
                                    <code className="detail-code">{selectedKey.api_key}</code>
                                    <button
                                        className="btn-copy-small"
                                        onClick={() => copyToClipboard(selectedKey.api_key, 'apiKey')}
                                    >
                                        {copied === 'apiKey' ? (
                                            <>
                                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Copié
                                            </>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                Copier
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="detail-row">
                                <label>Secret (masqué)</label>
                                <div className="detail-value">
                                    <code className="detail-code secret">{maskSecret(selectedKey.api_secret_prefix)}</code>
                                    <span className="detail-hint">Le secret complet n'est affiché qu'à la création</span>
                                </div>
                            </div>

                            <div className="detail-row">
                                <label>Créée par</label>
                                <div className="detail-value">
                                    {selectedKey.created_by.first_name} {selectedKey.created_by.last_name}
                                </div>
                            </div>

                            <div className="detail-row">
                                <label>Date de création</label>
                                <div className="detail-value">{formatDate(selectedKey.created_at)}</div>
                            </div>

                            <div className="detail-row">
                                <label>Dernière utilisation</label>
                                <div className="detail-value">
                                    {selectedKey.last_used_at ? (
                                        <>
                                            {formatDate(selectedKey.last_used_at)}
                                            {selectedKey.last_used_ip && (
                                                <span className="detail-hint">IP: {selectedKey.last_used_ip}</span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="never-used">Jamais utilisée</span>
                                    )}
                                </div>
                            </div>

                            <div className="detail-row">
                                <label>Statut</label>
                                <div className="detail-value">
                                    {selectedKey.is_active ? (
                                        <span className="status-badge active">Active</span>
                                    ) : (
                                        <span className="status-badge revoked">
                                            Révoquée le {formatDate(selectedKey.revoked_at)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            {selectedKey.is_active && (
                                <button
                                    className="btn-danger"
                                    onClick={() => {
                                        handleRevokeKey(selectedKey.id, selectedKey.name);
                                        setSelectedKey(null);
                                    }}
                                >
                                    Révoquer cette clé
                                </button>
                            )}
                            <button className="btn-secondary" onClick={() => setSelectedKey(null)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApiKeysSection;
